import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Camera, X, Keyboard } from 'lucide-react';

async function abrirCameraTraseira() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Este navegador não tem câmera.');
  }
  const tentativas = [
    { video: { facingMode: { exact: 'environment' } }, audio: false },
    { video: { facingMode: { ideal: 'environment' } }, audio: false },
    { video: { facingMode: 'environment' }, audio: false },
    { video: true, audio: false },
  ];
  let ultimoErro = null;
  for (const constraints of tentativas) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      ultimoErro = e;
    }
  }
  throw ultimoErro || new Error('Não foi possível abrir a câmera.');
}

function lerFrameComJsQr(video, canvas) {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return '';
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return '';
  ctx.drawImage(video, 0, 0, w, h);
  const image = ctx.getImageData(0, 0, w, h);
  const code = jsQR(image.data, image.width, image.height, { inversionAttempts: 'attemptBoth' });
  return code?.data ? String(code.data).trim() : '';
}

/**
 * Abre a câmera do dispositivo e lê QR (BarcodeDetector ou jsQR).
 * Digitação fica só como alternativa se a câmera falhar ou o operador preferir.
 */
const ScanQrModal = ({ open, title, onRead, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const onReadRef = useRef(onRead);
  const [erro, setErro] = useState('');
  const [manual, setManual] = useState('');
  const [usandoManual, setUsandoManual] = useState(false);
  const [cameraPronta, setCameraPronta] = useState(false);

  onReadRef.current = onRead;

  useEffect(() => {
    if (!open) {
      setErro('');
      setManual('');
      setUsandoManual(false);
      setCameraPronta(false);
    }
  }, [open]);

  useEffect(() => {
    let cancelado = false;
    let timer;
    let detector = null;

    const pararStream = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    };

    async function start() {
      setErro('');
      setCameraPronta(false);
      if (!open || usandoManual) {
        pararStream();
        return;
      }

      try {
        const stream = await abrirCameraTraseira();
        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.srcObject = stream;
        await video.play().catch(() => {});
        if (cancelado) return;
        setCameraPronta(true);

        if (typeof window.BarcodeDetector === 'function') {
          try {
            detector = new window.BarcodeDetector({ formats: ['qr_code'] });
          } catch {
            detector = null;
          }
        }

        const tick = async () => {
          if (cancelado || !videoRef.current) return;
          const videoEl = videoRef.current;
          if (videoEl.readyState >= 2) {
            try {
              if (detector) {
                const codes = await detector.detect(videoEl);
                const valor = codes?.[0]?.rawValue ? String(codes[0].rawValue).trim() : '';
                if (valor) {
                  onReadRef.current(valor);
                  return;
                }
              }
              const canvas = canvasRef.current;
              if (canvas) {
                const valor = lerFrameComJsQr(videoEl, canvas);
                if (valor) {
                  onReadRef.current(valor);
                  return;
                }
              }
            } catch {
              /* frame ainda vazio */
            }
          }
          timer = window.setTimeout(tick, 220);
        };
        tick();
      } catch (e) {
        if (cancelado) return;
        const bloqueada = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
        setErro(
          bloqueada
            ? 'Permita o uso da câmera neste navegador e toque de novo em Ler.'
            : 'Não foi possível abrir a câmera. Use a digitação do código ou outro navegador (Chrome).'
        );
        setUsandoManual(true);
      }
    }

    start();
    return () => {
      cancelado = true;
      if (timer) clearTimeout(timer);
      pararStream();
    };
  }, [open, usandoManual]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-3">
      <div className="w-full max-w-md rounded-2xl bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Camera className="h-5 w-5 text-emerald-600" />
            {title || 'Ler QR'}
          </h3>
          <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className={usandoManual ? 'hidden' : 'relative bg-black'}>
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="w-full h-72 bg-black object-cover"
          />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-44 w-44 rounded-xl border-2 border-emerald-400/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.28)]" />
          </div>
          <p className="absolute bottom-2 left-0 right-0 text-center text-xs text-white drop-shadow">
            {cameraPronta ? 'Aponte a câmera para o QR da etiqueta' : 'Abrindo câmera…'}
          </p>
        </div>
        <canvas ref={canvasRef} className="hidden" />

        {usandoManual && (
          <div className="p-4 space-y-2">
            <p className="text-sm text-gray-600">Cole ou digite o código impresso na etiqueta.</p>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="RM:PAL:… ou PAL-20260919-001"
              autoFocus
            />
            <button
              type="button"
              className="w-full bg-emerald-600 text-white rounded-lg py-2 text-sm font-semibold"
              onClick={() => manual.trim() && onReadRef.current(manual.trim())}
            >
              Confirmar código
            </button>
            <button
              type="button"
              className="w-full border rounded-lg py-2 text-sm"
              onClick={() => {
                setUsandoManual(false);
                setErro('');
              }}
            >
              Voltar para a câmera
            </button>
          </div>
        )}

        {erro ? <p className="px-4 py-2 text-xs text-red-700">{erro}</p> : null}

        <div className="px-4 py-3 border-t flex justify-between">
          <button
            type="button"
            className="text-sm text-gray-600 inline-flex items-center gap-1"
            onClick={() => setUsandoManual(true)}
          >
            <Keyboard className="h-4 w-4" /> Digitar código
          </button>
          <button type="button" className="text-sm border rounded-lg px-3 py-1.5" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScanQrModal;
